import React, { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { ParseNumber } from "../../../utils/ParseNumber.js";

function useInViewport(ref, options = { threshold: 0.2 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      options
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, options.threshold]);
  return inView;
}

function Card({ statistic }) {
  const wrapperRef = useRef(null);
  const inView = useInViewport(wrapperRef, { threshold: 0.2 });
  const startRef = useRef(null);

  const target = Number(ParseNumber(statistic?.count ?? 0)) || 0;

  return (
    <div
      ref={wrapperRef}
      className="group relative flex flex-col items-start justify-between h-full bg-white dark:bg-zinc-900/60 backdrop-blur-xl border border-slate-200/60 dark:border-emerald-500/10 hover:border-emerald-400/40 dark:hover:border-emerald-500/40 rounded-2xl p-6 transition-all duration-500 ease-out hover:bg-emerald-50/50 dark:hover:bg-emerald-500/10 overflow-hidden shadow-xl shadow-slate-200/50 dark:shadow-none hover:-translate-y-1"
    >
      {/* Background glow on hover */}
      <div className="absolute -inset-x-4 -inset-y-4 z-0 bg-gradient-to-b from-emerald-500/0 to-emerald-500/5 dark:to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none blur-2xl"></div>

      {/* Modern Integrated Icon */}
      <div className="relative z-10 bg-emerald-50 dark:bg-emerald-500/10 p-3.5 rounded-xl border border-emerald-100 dark:border-emerald-500/20 mb-8 shadow-sm dark:shadow-[0_0_15px_rgba(16,185,129,0.1)] group-hover:shadow-[0_0_25px_rgba(16,185,129,0.25)] transition-shadow duration-500">
        <img
          src={process.env.REACT_APP_IMG_URL + statistic.icon}
          alt={statistic.name}
          className="w-7 h-7 md:w-8 md:h-8 object-contain opacity-70 dark:opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 brightness-0 dark:invert"
        />
      </div>

      {/* Massive Metrics & Typography */}
      <div className="relative z-10 w-full mt-auto">
        <h3 className="text-4xl lg:text-[2.6rem] font-black text-slate-800 dark:text-white tracking-tight mb-2 drop-shadow-sm dark:drop-shadow-md transition-colors duration-500">
          <CountUp start={0} end={target} duration={2.5} separator="," preserveValue={true}>
            {({ countUpRef, start }) => {
              startRef.current = start;
              return <span ref={countUpRef}>0</span>;
            }}
          </CountUp>
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base font-bold leading-relaxed group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors duration-300">
          {statistic.name}
        </p>
      </div>
    </div>
  );
}

export default Card;
